import { AlertTriangle, ArrowLeft, ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ClusterTopologyCard } from "@/components/ci/cluster-topology-card";
import { DeployInfoCard } from "@/components/ci/deploy-info-card";
import { FailurePhaseBadge } from "@/components/ci/failure-phase-badge";
import { MachineTimeline } from "@/components/ci/machine-timeline";
import { MatrixCellGroup } from "@/components/ci/matrix-cell-group";
import { NodeTimeline } from "@/components/ci/node-timeline";
import { RestorePerfTable } from "@/components/ci/restore-perf-table";
import { RunSummaryBar } from "@/components/ci/run-summary-bar";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { TestTypeBadge } from "@/components/ci/test-type-badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCiRun } from "@/hooks/use-ci";
import { usePageTitle } from "@/hooks/use-page-title";
import type { CiTestResult } from "@/lib/types";
import { cn, formatDurationFromRange, formatMs } from "@/lib/utils";

function VersionGroup({
  version,
  results,
  runId,
}: {
  version: string;
  results: CiTestResult[];
  runId: string;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const passed = results.filter((result) => result.status === "pass").length;
  const failed = results.filter(
    (result) => result.status === "fail" || result.status === "error",
  ).length;
  const xfailed = results.filter((result) => result.status === "xfail").length;
  const xpassed = results.filter((result) => result.status === "xpass").length;
  const skipped = results.filter((result) => result.status === "skip").length;

  // Sort by severity: red first, then known-issue statuses, then pass, then skip.
  const sorted = useMemo(() => {
    const priority: Record<string, number> = {
      fail: 0,
      error: 0,
      xfail: 1,
      xpass: 2,
      pass: 3,
      skip: 4,
    };
    return [...results].sort(
      (first, second) => (priority[first.status] ?? 3) - (priority[second.status] ?? 3),
    );
  }, [results]);

  return (
    <div className="rounded-lg border">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm font-medium">{version}</span>
          <div className="flex items-center gap-1.5 text-xs">
            {passed > 0 && <span className="text-emerald-500">{passed} passed</span>}
            {failed > 0 && <span className="text-rose-500">{failed} failed</span>}
            {xfailed > 0 && <span className="text-yellow-600">{xfailed} xfail</span>}
            {xpassed > 0 && <span className="text-cyan-600">{xpassed} xpass</span>}
            {skipped > 0 && <span className="text-muted-foreground">{skipped} skipped</span>}
          </div>
        </div>
        <span className="text-xs text-muted-foreground">{collapsed ? "Show" : "Hide"}</span>
      </button>
      {!collapsed && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Test</TableHead>
              <TableHead>Operation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Duration</TableHead>
              <TableHead className="text-right">Restore</TableHead>
              <TableHead className="text-right">Inference</TableHead>
              <TableHead>Machine</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((result) => (
              <TableRow
                key={result.id}
                className={cn(
                  "cursor-pointer",
                  (result.status === "fail" || result.status === "error") &&
                    "border-l-2 border-l-rose-500",
                  result.status === "xfail" && "border-l-2 border-l-yellow-500",
                  result.status === "xpass" && "border-l-2 border-l-cyan-500",
                )}
                onClick={() => navigate(`/ci/${runId}/tests/${result.id}`)}
              >
                <TableCell>
                  <div>
                    <span className="font-mono text-sm">{result.test_name}</span>
                    {(result.status === "fail" || result.status === "error") &&
                      (result.error_classification_detail || result.error) && (
                        <p className="mt-0.5 max-w-md truncate text-xs text-rose-500">
                          {result.error_classification_detail || result.error}
                        </p>
                      )}
                    {result.status === "xfail" && (
                      <p className="mt-0.5 max-w-md truncate text-xs text-yellow-600">
                        expected failure ({result.known_issue_id ?? "known issue"})
                        {result.error ? ` — ${result.error}` : ""}
                      </p>
                    )}
                    {result.status === "xpass" && (
                      <p className="mt-0.5 max-w-md truncate text-xs text-cyan-600">
                        expected failure PASSED — {result.known_issue_id ?? "ticket"} may be fixable
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-muted-foreground">{result.operation}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <CiStatusBadge status={result.status} />
                    {result.status === "fail" && result.failure_phase && (
                      <FailurePhaseBadge phase={result.failure_phase} />
                    )}
                    {/* INF- ids predate Jira and have no Jira page: shown unlinked. */}
                    {result.known_issue_id && !result.known_issue_id.startsWith("INF-") && (
                      <a
                        href={`https://nebius.atlassian.net/browse/${result.known_issue_id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center rounded-md border border-yellow-500/20 bg-yellow-500/10 px-2 py-0.5 text-xs font-medium text-yellow-600 hover:underline"
                      >
                        {result.known_issue_id}
                      </a>
                    )}
                    {result.known_issue_id?.startsWith("INF-") && (
                      <span className="inline-flex items-center rounded-md border border-yellow-500/20 bg-yellow-500/10 px-2 py-0.5 text-xs font-medium text-yellow-600">
                        {result.known_issue_id}
                      </span>
                    )}
                    {result.flaky && (
                      <span className="inline-flex items-center rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        flaky ×{result.attempts}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {result.duration_seconds > 0 ? `${result.duration_seconds.toFixed(1)}s` : "-"}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {formatMs(result.total_restore_ms)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {formatMs(result.inference_ms)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {result.machine_name || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export function CiRunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const { data: run, isLoading } = useCiRun(runId);
  usePageTitle(run ? `CI Run ${run.run_id.slice(0, 8)}` : "CI Run");

  // Group results by version (criu) or by matrix cell (cryod)
  const versionGroups = useMemo(() => {
    if (!run?.test_results) return [];
    const groups = new Map<string, CiTestResult[]>();
    for (const result of run.test_results) {
      const existing = groups.get(result.version) ?? [];
      existing.push(result);
      groups.set(result.version, existing);
    }
    return Array.from(groups.entries());
  }, [run]);

  const matrixCellGroups = useMemo(() => {
    if (!run?.test_results) return [];
    const groups = new Map<
      string,
      { results: CiTestResult[]; axisValues: Record<string, string> }
    >();
    for (const result of run.test_results) {
      const key = result.matrix_cell_id || result.version || "default";
      const existing = groups.get(key);
      if (existing) {
        existing.results.push(result);
      } else {
        groups.set(key, {
          results: [result],
          axisValues: result.matrix_axis_values ?? {},
        });
      }
    }
    return Array.from(groups.entries()).map(([cellId, payload]) => ({
      cellId,
      results: payload.results,
      axisValues: payload.axisValues,
    }));
  }, [run]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-screen-xl space-y-6 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!run) {
    return <div className="py-16 text-center text-sm text-muted-foreground">Run not found</div>;
  }

  return (
    <div className="mx-auto max-w-screen-xl space-y-6 p-6">
      {/* Header */}
      <div>
        <Link
          to="/ci"
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          CI Runs
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold font-mono">#{run.run_id}</h1>
          <TestTypeBadge type={run.test_type ?? "criu"} />
          <CiStatusBadge status={run.status} />
          {run.git_branch && (
            <span className="text-sm text-muted-foreground">{run.git_branch}</span>
          )}
          {run.git_sha && (
            <span className="font-mono text-xs text-muted-foreground">
              {run.git_sha.slice(0, 7)}
            </span>
          )}
          {run.started_at && (
            <span className="text-sm text-muted-foreground">
              {new Date(run.started_at).toLocaleString(undefined, {
                timeZone: "UTC",
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              (UTC)
            </span>
          )}
          {run.run_url && (
            <a
              href={run.run_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="size-4" />
            </a>
          )}
        </div>
      </div>

      {/* Timeout banner — runner stopped reporting heartbeats; runs-timeout
         watchdog flipped this row to timed_out. Tests below may be partial. */}
      {run.status === "timed_out" && (
        <div className="flex items-start gap-3 rounded-md border border-slate-500/20 bg-slate-500/5 p-4">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-slate-400" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-300">Run cancelled by timeout</p>
            <p className="text-xs text-muted-foreground">
              The runner stopped reporting progress and the fleet auto-cancelled this run. Test
              results below reflect what was reported before the runner went silent — they may be
              partial.
            </p>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Passed</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-emerald-500">{run.passed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <p
              className={cn(
                "text-3xl font-bold",
                run.failed > 0 ? "text-rose-500" : "text-muted-foreground",
              )}
            >
              {run.failed}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Skipped</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-muted-foreground">{run.skipped}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Duration</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {formatDurationFromRange(run.started_at, run.completed_at)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pass rate bar */}
      <RunSummaryBar
        passed={run.passed}
        failed={run.failed}
        skipped={run.skipped}
        total={run.total_tests}
        xfailed={run.xfailed}
        xpassed={run.xpassed}
      />

      {run.test_type === "cryod" ? (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <ClusterTopologyCard cluster={run.cluster_info} />
            </div>
            <DeployInfoCard deploy={run.deploy_info} />
          </div>
          {/* A restore-perf run's report: rendered only when its rows carry the perf tags. */}
          <RestorePerfTable results={run.test_results} runId={run.run_id} />
          {/* The per-node timeline is built from cryod restore phases; a Mooncake run has none. */}
          {run.run_kind !== "mctest" && <NodeTimeline run={run} />}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Test results</h2>
            {matrixCellGroups.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No test results
                {run.status === "in_progress" && " — tests are still running"}
                {run.status === "timed_out" && " — runner went silent before reporting any tests"}
              </p>
            ) : (
              matrixCellGroups.map((group) => (
                <MatrixCellGroup
                  key={group.cellId}
                  cellId={group.cellId}
                  axisValues={group.axisValues}
                  results={group.results}
                  runId={run.run_id}
                />
              ))
            )}
          </div>
        </>
      ) : (
        <>
          <MachineTimeline run={run} />
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Test Results</h2>
            {versionGroups.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No test results
                {run.status === "in_progress" && " — tests are still running"}
                {run.status === "timed_out" && " — runner went silent before reporting any tests"}
              </p>
            ) : (
              versionGroups.map(([version, results]) => (
                <VersionGroup
                  key={version}
                  version={version}
                  results={results}
                  runId={run.run_id}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
